import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Star, Phone, Mail, MessageSquare, Search } from "lucide-react";
import { LEAD_STAGES, fmtDate } from "../../lib/constants";
import { toast } from "sonner";

const STAGE_MAP = Object.fromEntries(LEAD_STAGES.map((s) => [s.key, s.label]));

const STAGE_CLASS = {
  new: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  contacted: "bg-slate-500/10 text-slate-300 border-slate-500/20",
  interested: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  follow_up: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  not_interested: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  converted: "bg-violet-500/10 text-violet-300 border-violet-500/20",
};

export default function SponsorLeads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [sort, setSort] = useState("priority");
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sponsor/leads");
      setLeads(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let arr = leads.filter((l) => {
      if (stage !== "all" && l.status !== stage) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          l.name?.toLowerCase().includes(s) ||
          l.city?.toLowerCase().includes(s) ||
          l.state?.toLowerCase().includes(s) ||
          l.investor_type?.toLowerCase().includes(s)
        );
      }
      return true;
    });
    switch (sort) {
      case "name_asc": arr.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "name_desc": arr.sort((a, b) => b.name.localeCompare(a.name)); break;
      case "city": arr.sort((a, b) => (a.city || "").localeCompare(b.city || "")); break;
      case "stage": arr.sort((a, b) => (a.status || "").localeCompare(b.status || "")); break;
      case "experience": arr.sort((a, b) => (a.experience || "").localeCompare(b.experience || "")); break;
      case "investor": arr.sort((a, b) => (a.investor_type || "").localeCompare(b.investor_type || "")); break;
      case "priority": arr.sort((a, b) => (b.priority === "high" ? 1 : 0) - (a.priority === "high" ? 1 : 0)); break;
      case "recent":
        arr.sort((a, b) => (new Date(b.last_contacted || 0)) - (new Date(a.last_contacted || 0))); break;
      default: break;
    }
    return arr;
  }, [leads, q, stage, sort]);

  const openLead = (l) => {
    setSel(l);
    setNotes(l.notes || "");
    setOpen(true);
  };

  const save = async (patch) => {
    if (!sel) return;
    try {
      const { data } = await api.patch(`/sponsor/leads/${sel.lead_id}`, patch);
      setLeads((arr) => arr.map((x) => x.lead_id === data.lead_id ? data : x));
      setSel(data);
      toast.success("Lead updated");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Leads · CRM</h1>
          <p className="mt-1 text-sm text-slate-400">Your 100 qualified investor leads.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>{filtered.length} / {leads.length}</span>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-6">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search by name, city, state, investor type…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="border-slate-800 bg-slate-950 pl-9 text-slate-50"
                data-testid="leads-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="border-slate-800 bg-slate-950 text-slate-50" data-testid="leads-stage-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                <SelectItem value="all">All Stages</SelectItem>
                {LEAD_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="border-slate-800 bg-slate-950 text-slate-50" data-testid="leads-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                <SelectItem value="priority">Priority First</SelectItem>
                <SelectItem value="recent">Recently Contacted</SelectItem>
                <SelectItem value="name_asc">Name A–Z</SelectItem>
                <SelectItem value="name_desc">Name Z–A</SelectItem>
                <SelectItem value="city">City</SelectItem>
                <SelectItem value="stage">Stage</SelectItem>
                <SelectItem value="experience">Experience</SelectItem>
                <SelectItem value="investor">Investor Type</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-slate-800 bg-slate-900">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="w-8 text-slate-400"></TableHead>
              <TableHead className="text-slate-400">Name</TableHead>
              <TableHead className="text-slate-400">Contact</TableHead>
              <TableHead className="text-slate-400">Location</TableHead>
              <TableHead className="text-slate-400">Type</TableHead>
              <TableHead className="text-slate-400">Experience</TableHead>
              <TableHead className="text-slate-400">Interested</TableHead>
              <TableHead className="text-slate-400">Stage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow
                key={l.lead_id}
                onClick={() => openLead(l)}
                className="cursor-pointer border-slate-800 hover:bg-slate-800/50"
                data-testid={`lead-row-${l.lead_id}`}
              >
                <TableCell>
                  {l.priority === "high" ? (
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ) : (
                    <Star className="h-4 w-4 text-slate-600" />
                  )}
                </TableCell>
                <TableCell className="font-medium text-slate-100">{l.name}</TableCell>
                <TableCell className="text-xs text-slate-400">
                  <div>{l.phone}</div>
                  <div>{l.email}</div>
                </TableCell>
                <TableCell className="text-slate-300 text-sm">{l.city}, {l.state}</TableCell>
                <TableCell className="text-slate-300 text-sm">{l.investor_type}</TableCell>
                <TableCell className="text-slate-300 text-sm">{l.experience}</TableCell>
                <TableCell className="text-slate-300 text-sm">{l.interested_in}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STAGE_CLASS[l.status] || ""} text-xs`}>
                    {STAGE_MAP[l.status] || l.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg border-l border-slate-800 bg-slate-900 text-slate-50 overflow-y-auto" data-testid="lead-detail-sheet">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle className="text-slate-100 flex items-center gap-2">
                  {sel.priority === "high" && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
                  {sel.name}
                </SheetTitle>
                <SheetDescription className="text-slate-400">{sel.city}, {sel.state}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 grid grid-cols-2 gap-2">
                <a href={`tel:${sel.phone}`} className="rounded-md border border-slate-800 bg-slate-950 p-3 text-center text-xs text-slate-200 hover:border-emerald-500/30" data-testid="lead-call">
                  <Phone className="mx-auto h-4 w-4 text-emerald-400 mb-1" /> Call
                </a>
                <a href={`mailto:${sel.email}`} className="rounded-md border border-slate-800 bg-slate-950 p-3 text-center text-xs text-slate-200 hover:border-emerald-500/30" data-testid="lead-email">
                  <Mail className="mx-auto h-4 w-4 text-emerald-400 mb-1" /> Email
                </a>
                <a href={`sms:${sel.phone}`} className="rounded-md border border-slate-800 bg-slate-950 p-3 text-center text-xs text-slate-200 hover:border-emerald-500/30" data-testid="lead-sms">
                  <MessageSquare className="mx-auto h-4 w-4 text-emerald-400 mb-1" /> Text
                </a>
                <button
                  onClick={() => save({ priority: sel.priority === "high" ? "normal" : "high" })}
                  className="rounded-md border border-slate-800 bg-slate-950 p-3 text-center text-xs text-slate-200 hover:border-amber-500/30"
                  data-testid="lead-toggle-priority"
                >
                  <Star className={`mx-auto h-4 w-4 mb-1 ${sel.priority === "high" ? "fill-amber-400 text-amber-400" : "text-slate-400"}`} />
                  {sel.priority === "high" ? "High Priority" : "Mark Priority"}
                </button>
              </div>

              <div className="mt-6">
                <Label className="text-slate-300 text-xs uppercase tracking-widest">Lead Stage</Label>
                <Select value={sel.status} onValueChange={(v) => save({ status: v })}>
                  <SelectTrigger className="mt-2 border-slate-800 bg-slate-950 text-slate-50" data-testid="lead-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                    {LEAD_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                <DRow label="Experience" value={sel.experience} />
                <DRow label="Interested" value={sel.interested_in} />
                <DRow label="Investor Type" value={sel.investor_type} />
                <DRow label="Last Contacted" value={fmtDate(sel.last_contacted)} />
                <DRow label="Phone" value={sel.phone} />
                <DRow label="Email" value={sel.email} />
              </div>

              <div className="mt-6">
                <Label className="text-slate-300 text-xs uppercase tracking-widest">Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-2 min-h-[140px] border-slate-800 bg-slate-950 text-slate-50"
                  data-testid="lead-notes-textarea"
                />
                <Button onClick={() => save({ notes })} className="mt-3 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="lead-save-notes-btn">
                  Save Notes
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DRow({ label, value }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-slate-100">{value || "—"}</div>
    </div>
  );
}
